import React from 'react'
import { col } from './styles'
import { useStore } from '@/store'
import { ClientGlyph, StudioGlyph, type ClientGrain } from './glyphs'

interface Props {
  openCount: number
  onToggleIncidents: () => void
  isUpdating: boolean
  hasError: boolean
}

const HOP_PILLS: Array<{ key: 'studios' | 'clients' | 'flows'; label: string }> = [
  { key: 'studios', label: 'Studio' },
  { key: 'clients', label: 'Clients' },
  { key: 'flows', label: 'Delivery' },
]

const CLIENT_REGIONS: Array<{ key: ClientGrain; label: string; title: string }> = [
  { key: 'continent', label: 'Continent', title: 'Group client clusters by continent' },
  { key: 'country', label: 'Country', title: 'Group client clusters by country' },
  { key: 'city', label: 'City', title: 'Group client clusters by city' },
]

const NEUTRAL = '#8fa2bf'

function pillStyle(active: boolean, accent?: boolean): React.CSSProperties {
  return {
    marginLeft: 6,
    fontSize: 10,
    padding: '2px 8px',
    borderRadius: 999,
    cursor: 'pointer',
    border: `0.5px solid ${active && accent ? 'var(--crit-bd)' : active ? 'var(--bd-2, rgba(255,255,255,0.18))' : 'var(--bd-1)'}`,
    background: active ? (accent ? 'var(--crit-bg)' : 'rgba(255,255,255,0.08)') : 'transparent',
    color: active ? (accent ? 'var(--crit)' : 'var(--t-1)') : 'var(--t-2)',
    fontWeight: active ? 500 : 400,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    userSelect: 'none',
  }
}

function ChartIcon({ kind }: { kind: 'studios' | 'clients' | 'flows' }) {
  if (kind === 'studios') {
    return (
      <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden>
        <StudioGlyph cx={6} cy={6} r={5} fill={NEUTRAL} fillOpacity={0.35} stroke={NEUTRAL} />
      </svg>
    )
  }
  if (kind === 'clients') {
    return (
      <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden>
        <ClientGlyph cx={6} cy={6} r={4.5} grain="city" fill={NEUTRAL} fillOpacity={0.35} stroke={NEUTRAL} />
      </svg>
    )
  }
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden>
      <path d="M1 9 Q6 1 11 9" fill="none" stroke={NEUTRAL} strokeWidth={1.4} />
      <circle cx={6} cy={5} r={1.4} fill={NEUTRAL} />
    </svg>
  )
}

export function PanelHeader({
  openCount, onToggleIncidents,
  isUpdating, hasError,
}: Props) {
  const setView = useStore((s) => s.setView)
  const setDimension = useStore((s) => s.setDimension)
  const clearDrillNode = useStore((s) => s.clearDrillNode)
  const worldLayers = useStore((s) => s.worldLayers)
  const toggleWorldLayer = useStore((s) => s.toggleWorldLayer)
  const worldClientGrain = useStore((s) => s.worldClientGrain)
  const setWorldClientGrain = useStore((s) => s.setWorldClientGrain)
  const hasIncidents = openCount > 0

  return (
    <div className="panel-head" style={{ flexWrap: 'wrap', rowGap: 6 }}>
      <span className="panel-title">World overview</span>
      <span onClick={onToggleIncidents} style={pillStyle(hasIncidents, true)}>
        Incidents
        {hasIncidents && (
          <span style={{
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.22)',
            border: '0.5px solid rgba(0,0,0,0.25)',
            color: 'inherit',
            fontWeight: 600,
          }}>
            {openCount}
          </span>
        )}
      </span>

      <span style={{ marginLeft: 10, fontSize: 10, color: col.t2 }}>Chart</span>
      {HOP_PILLS.map((p) => (
        <span
          key={p.key}
          onClick={() => toggleWorldLayer(p.key)}
          title={
            p.key === 'flows'
              ? 'Delivery paths (network + CDN arcs)'
              : p.key === 'studios'
                ? 'Studio sources'
                : 'Client clusters'
          }
          style={pillStyle(worldLayers[p.key])}
        >
          <ChartIcon kind={p.key} />
          {p.label}
        </span>
      ))}

      <span style={{ marginLeft: 10, fontSize: 10, color: col.t2 }}>Client region</span>
      {CLIENT_REGIONS.map((g) => (
        <span
          key={g.key}
          onClick={() => setWorldClientGrain(g.key)}
          title={g.title}
          style={pillStyle(worldClientGrain === g.key)}
        >
          {g.label}
        </span>
      ))}

      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {isUpdating && <span style={{ fontSize: 10, color: col.t2, opacity: 0.8 }}>Updating…</span>}
        {hasError && <span style={{ fontSize: 10, color: 'var(--crit)' }}>API error</span>}
        <span
          onClick={() => {
            clearDrillNode()
            setDimension('stream')
            setView('hex')
          }}
          title="Open Hex with all streams matching the current filters"
          style={pillStyle(false)}
        >
          Open in Hex →
        </span>
      </span>
    </div>
  )
}
